/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */

import ErrorName from '../core/error/error-name';
import FileSystemPathFactory from '../core/file/system/file-system-path-factory';
import { waitForStorage } from '../core/storage/storage';
import { mixinType } from '../util.type';
import PlayerRoot from './player-root';

/**
 * @import { AppManager } from '../core/app'
 * @import AssetDownloader from '../core/asset/downloader/asset-downloader'
 * @import BrowserCapabilities from '../core/capabilities/browser-capabilities'
 * @import Communicator from '../core/communicator/communicator'
 * @import ContentSettingsStore from '../core/content/content-settings-store'
 * @import { ContentInfo } from '../core/content/info/content-info'
 * @import ContentDownloader from '../core/content/downloader/content-downloader';
 * @import { FileSystemManager, FileSystemName } from '../core/file/system'
 * @import { FingerprintManager } from '../core/fingerprint'
 * @import { Logger } from '../core/logger/logger'
 * @import LoggerManager from '../core/logger/logger-manager'
 * @import { LogicalUnitMapper } from '../core/logical-unit-mapper/'
 * @import { NetworkListener } from '../core/network/network-listener'
 * @import ReportManager from '../core/report/report-manager';
 * @import ScreenManager from '../core/screen/screen-manager'
 * @import { IServerConnectionMonitor } from '../core/server'
 * @import { Session } from '../core/session/session'
 * @import { IStatusInfoDialogController } from '../core/status-info-dialog'
 * @import Store from '../core/store/store'
 * @import { UpdateUrlConfig } from '../core/update-url-config'
 * @import { ViewerRegistry } from '../core/viewer/registry/'
 * @import { VirtualKeyboardManager } from '../core/virtual-keyboard'
 * @import VueFactory from '../core/vue/vue-factory'
 * @import { Device } from '../device'
 */

/**
 * @typedef {Pick<Session, 'deviceId' | 'fingerprint' | 'id' | 'serverUrl' | 'version'>} ISession
 */

/**
 * @typedef {Record<string, *>} ControlManager TODO #555
 * @typedef {Record<string, *>} FocusManager TODO #555
 */

/**
 * @typedef {DataNonReactive} Data
 */

/**
 * @typedef {{
 *   appManager: AppManager;
 *   browserCapabilities: BrowserCapabilities;
 *   contentSettingsStore: ContentSettingsStore;
 *   communicator: Communicator;
 *   controlManager: ControlManager;
 *   device: Device;
 *   contentDownloader: ContentDownloader;
 *   fileSystemManager: FileSystemManager;
 *   fingerprint: string;
 *   focusManager: FocusManager;
 *   hasSupportedBrowser: boolean;
 *   logger: Logger;
 *   loggerManager: LoggerManager;
 *   networkListener: NetworkListener;
 *   reportManager: ReportManager;
 *   screenManager: ScreenManager;
 *   serverConnectionMonitor: IServerConnectionMonitor;
 *   session: ISession;
 *   statusInfoDialogController: IStatusInfoDialogController;
 *   store: Store;
 *   updateUrlConfig: UpdateUrlConfig;
 *   viewerRegistry: ViewerRegistry;
 *   virtualKeyboardManager: VirtualKeyboardManager;
 * }} DataNonReactive
 */

/**
 * @typedef {{
 *   deviceStart(contentInfo: ContentInfo | null, rootEl: HTMLElement): Promise<void>;
 * }} Methods
 */

// TODO #555: Možná je přesnější použít Vue {Create,}ComponentPublicInstance
// nebo něco podobného pro Vue instance (CombinedVueInstance?), ale zatím takto
// pragmaticky deklarované veřejné rozhraní.
/**
 * @typedef {{}
 *   & Pick<DataNonReactive,
 *     | 'appManager'
 *     | 'browserCapabilities'
 *     | 'communicator'
 *     | 'contentSettingsStore'
 *     | 'controlManager'
 *     | 'device'
 *     | 'contentDownloader'
 *     | 'fileSystemManager'
 *     | 'fingerprint'
 *     | 'focusManager'
 *     | 'logger'
 *     | 'networkListener'
 *     | 'reportManager'
 *     | 'screenManager'
 *     | 'serverConnectionMonitor'
 *     | 'session'
 *     | 'statusInfoDialogController'
 *     | 'store'
 *     | 'updateUrlConfig'
 *     | 'viewerRegistry'
 *     | 'virtualKeyboardManager'
 *   >
 *   & Pick<Methods,
 *     | 'deviceStart'
 *   >
 * } Root
 */

/**
 * Player aplikace.
 */
export default class Player {
  /**
   * @param {AppManager} appManager
   * @param {AssetDownloader} assetDownloader
   * @param {BrowserCapabilities} browserCapabilities
   * @param {Communicator} communicator
   * @param {ContentSettingsStore} contentSettingsStore
   * @param {ControlManager} controlManager Správce ovládání.
   * @param {Device} device
   * @param {ContentDownloader} contentDownloader
   * @param {FileSystemManager} fileSystemManager
   * @param {FingerprintManager} fingerprintManager
   * @param {FocusManager} focusManager
   * @param {Logger} logger Zaznamenávač chyb.
   * @param {LoggerManager} loggerManager
   * @param {LogicalUnitMapper} logicalUnitMapper
   * @param {NetworkListener} networkListener
   * @param {ReportManager} reportManager
   * @param {ScreenManager} screenManager Správce obrazovky.
   * @param {IServerConnectionMonitor} serverConnectionMonitor
   * @param {ISession} session Sezení s nastavením zařízení.
   * @param {IStatusInfoDialogController} statusInfoDialogController
   * @param {Store} store
   * @param {UpdateUrlConfig} updateUrlConfig
   * @param {ViewerRegistry} viewerRegistry
   * @param {VirtualKeyboardManager} virtualKeyboardManager
   * @param {VueFactory} vueFactory
   */
  constructor(
    appManager,
    assetDownloader,
    browserCapabilities,
    communicator,
    contentSettingsStore,
    controlManager,
    device,
    contentDownloader,
    fileSystemManager,
    fingerprintManager,
    focusManager,
    logger,
    loggerManager,
    logicalUnitMapper,
    networkListener,
    reportManager,
    screenManager,
    serverConnectionMonitor,
    session,
    statusInfoDialogController,
    store,
    updateUrlConfig,
    viewerRegistry,
    virtualKeyboardManager,
    vueFactory
  ) {
    this.appManager = appManager;
    this.assetDownloader = assetDownloader;
    this.browserCapabilities = browserCapabilities;
    this.communicator = communicator;
    this.contentSettingsStore = contentSettingsStore;
    this.controlManager = controlManager;
    this.device = device;
    this.contentDownloader = contentDownloader;
    this.fileSystemManager = fileSystemManager;
    this.fingerprintManager = fingerprintManager;
    this.focusManager = focusManager;
    this.logger = logger;
    this.loggerManager = loggerManager;
    this.logicalUnitMapper = logicalUnitMapper;
    this.networkListener = networkListener;
    this.reportManager = reportManager;
    this.screenManager = screenManager;
    this.serverConnectionMonitor = serverConnectionMonitor;
    this.session = session;
    this.statusInfoDialogController = statusInfoDialogController;
    this.store = store;
    this.updateUrlConfig = updateUrlConfig;
    this.viewerRegistry = viewerRegistry;
    this.virtualKeyboardManager = virtualKeyboardManager;
    this.vueFactory = vueFactory;

    this.handleError = this.handleError.bind(this);
  }

  /**
   * Vytvoří a spustí novou instanci Vue.
   * @private
   * @param {Element | string} el DOM element pro připnutí Vue.
   * @param {Object} deps
   * @param {string} deps.fingerprint
   * @private
   * @returns {Promise<void>} Vyřeší se po vytvoření a vložení do DOM.
   */
  startVue(el, deps) {
    const { fingerprint } = deps;
    const self = this;
    return new Promise((resolve) => {
      // eslint-disable-next-line no-new
      new this.vueFactory.Vue({
        components: { PlayerRoot },
        el,
        template: '<PlayerRoot v-on:error="onError" />',
        // Private scope for non-reactive data and methods
        // https://github.com/vuejs/vue/issues/1988#issuecomment-163013818
        created() {
          // TODO Předávat zde celou instanci BottleJs, místo zbytečného výčtu?
          this.appManager = self.appManager;
          this.browserCapabilities = self.browserCapabilities;
          this.communicator = self.communicator;
          this.contentSettingsStore = self.contentSettingsStore;
          this.controlManager = self.controlManager;
          this.device = self.device;
          this.contentDownloader = self.contentDownloader;
          this.fileSystemManager = self.fileSystemManager;
          this.fingerprint = fingerprint;
          this.focusManager = self.focusManager;
          this.logger = self.logger;
          this.loggerManager = self.loggerManager;
          this.networkListener = self.networkListener;
          this.reportManager = self.reportManager;
          this.screenManager = self.screenManager;
          this.serverConnectionMonitor = self.serverConnectionMonitor;
          this.session = self.session;
          this.statusInfoDialogController = self.statusInfoDialogController;
          this.store = self.store;
          this.updateUrlConfig = self.updateUrlConfig;
          this.viewerRegistry = self.viewerRegistry;
          this.virtualKeyboardManager = self.virtualKeyboardManager;
        },
        mounted() {
          // Vyřešení výsledné promise startVue.
          resolve();
        },
        destroyed() {
          this.device.destroy();
          this.serverConnectionMonitor.destroy();
          this.statusInfoDialogController.destroy();
        },
        data() {
          return mixinType({}, /** @type {DataNonReactive} */ ({}));
        },
        // TODO #555: Nějak ověřit, že metody splňují rozhraní Methods,
        // ale veškeré pokusy přes @type nebo @satisfies selhávají na problémy
        // s this.
        methods: {
          /**
           * @param {ContentInfo | null} contentInfo
           * @param {HTMLElement} rootEl
           */
          async deviceStart(contentInfo, rootEl) {
            // Přidání dodatečných informací pro logování chyb
            // a datového skladu pro zálohu výjimek.
            await this.loggerManager
              .getInfo()
              .then((loggerInfo) => this.logger.setInfo(loggerInfo));
            this.logger.debug('Device#start');
            await this.device.start(contentInfo, rootEl);
          },
          /**
           * @param {Error} error
           */
          onError(error) {
            self.handleError(error);
          }
        }
      });
    });
  }

  /**
   * Reakce na vzniklou chybu.
   * @param {Error} error
   */
  handleError(error) {
    if (error.name === ErrorName.NotSupportedError) {
      this.logger.warn(error);
      return;
    }

    this.logger.error(error);
  }

  /**
   * Spustí player aplikaci.
   * @param {HTMLElement | string} el DOM element pro připnutí aplikace.
   * @returns {Promise<void>} Vyřeší se po přidání spuštěné aplikace do DOM.
   */
  async start(el) {
    // Zkontroluje zda-li jsou assety validní, a případně je namiguruje.
    await this.assetDownloader.migrateAssets().catch(this.handleError);
    // Empricky zjištěný timeout v ms, po který nutné čekat, aby detekovat jestli
    // se zpozdilo připojení jednotek a nebo skutečně chybí. Průměrně zpoždění
    // na WebOS bylo 2500 ms, v extrémech dosahovalo na 4500 ms.
    const externalUnitsDelayTimeoutMs = 6000;
    // Čekání na připojení jednotek. Interní jednotka by měla být vždy dostupná,
    // takže by nemělo dojíž ke zpozdění.
    // Všechny chyby se ošetřují uvnitř funkce.
    await waitForStorage(
      this.logicalUnitMapper,
      await getUnitsFromAssets(this.store, this.logger),
      externalUnitsDelayTimeoutMs,
      this.logger
    );

    // Kontrola stažených assetů. Volá se úmyslně před načítáním obsahu, protože při restore může
    // dojít ke smazání assetu z localStorage, protože chybí fyzicky v úložišti (např. odpojené USB).
    await this.assetDownloader.checkAssets().catch(this.handleError);

    // Kontrola otisku zařízení.
    let fingerprint = this.session.fingerprint;
    // Pokud již otisk existuje, vrátí se původní otisk zařízení.
    if (fingerprint === null) {
      // Vygenerování otisku zařízení.
      fingerprint = /** @type {string} TODO #555 */ (
        await this.fingerprintManager.generate()
      );
    }

    // Vykreslení GUI.
    await this.startVue(el, { fingerprint });
  }
}

/**
 * Získá Vue $root přetypovaný na Player root.
 * @param {{ $root: any }} instance
 * @returns {Root}
 */
export function $root(instance) {
  return instance.$root;
}

/**
 * Získá množinu jednotek, na kterých byly uloženy nějaké assety.
 *
 * Metoda nevyhodí výjimku. V případě chyby je zalogovaná
 * předaným loggerem.
 * @param {Store} store
 * @param {Logger} logger
 * @returns {Promise<Set<FileSystemName>>}
 */
async function getUnitsFromAssets(store, logger) {
  const assets = await store.findAllAsset().catch((error) => {
    logger.error(error);
    return [];
  });
  return new Set(
    assets
      .map((asset) => FileSystemPathFactory.fromValue(asset.path).getUnit())
      .filter((unit) => unit !== '')
  );
}
